'use client';

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Header, {HeaderConfigType, SmallHeader, HeaderProps} from '@nib-components/header';
import Footer, {CustomFooterConfig, SmallFooter, FooterProps} from '@nib-components/footer';
import GrowLayout from '@nib-components/grow-layout';
import {SkipLink, Target} from '@nib-components/skip-link';
import {ChatLauncher, Types} from '@nib/chatbot-launcher';

export const validVariationValues = ['arhi', 'shareholders', 'nz', 'foundation', 'thrive', 'providers', 'international', 'internationalStudents', 'internationalWorkers', 'gu'] as const;
export type variationValues = (typeof validVariationValues)[number];

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

const MainGrowLayoutContent = (props: any) => <GrowLayout.Content as="main" {...props} />;

export interface HeaderFooterLayoutProps {
  headerConfig?: HeaderConfigType;
  footerConfig?: CustomFooterConfig;
  variation?: variationValues;
  children?: React.ReactNode;
  header?: React.ReactNode | React.ComponentType<HeaderProps>;
  footer?: React.ReactNode | React.ComponentType<FooterProps>;
  smallLayout?: boolean;
  showSmallHeaderMenu?: boolean;
  enableSearch?: boolean;
  hideHeaderPhoneNumber?: boolean;
  flex?: boolean;
  isCorporate?: boolean;
  invertSmallHeader?: boolean;
  invert?: boolean;
  nzShielded?: React.ReactNode;
  withAbsoluteLinks?: boolean;
  absoluteBaseUrl?: string;
  enableNibby?: boolean;
  nibbyOptions?: Types.IProps;
  [key: string]: unknown;
}

const isReactNode = (component: React.ReactNode): boolean => React.isValidElement(component);

const HeaderFooterLayout: React.FC<HeaderFooterLayoutProps> = ({
  children,
  headerConfig,
  footerConfig,
  variation = 'arhi',
  header,
  footer,
  smallLayout,
  showSmallHeaderMenu,
  enableSearch,
  hideHeaderPhoneNumber,
  flex,
  isCorporate,
  invertSmallHeader,
  invert,
  nzShielded,
  withAbsoluteLinks,
  absoluteBaseUrl,
  enableNibby,
  nibbyOptions,
  ...otherProps
}) => {
  const defaultHeader = smallLayout ? SmallHeader : Header;
  const defaultFooter = smallLayout ? SmallFooter : Footer;

  const ConfiguredHeader: any = header ? header : defaultHeader;
  const ConfiguredFooter: any = footer ? footer : defaultFooter;

  // Invert if either prop is true
  const invertHeaderOrSmallHeader = invert || invertSmallHeader;
  return (
    <Wrapper {...otherProps}>
      <SkipLink link="#content" text="Skip to content" />
      <GrowLayout.Wrapper>
        {header && isReactNode(header) ? (
          // No props are passed to node header prop
          // If we wanted props, we could use React.cloneElement
          header
        ) : (
          // We need to pass both `invert` and `invertSmallHeader` as we don't know if `ConfiguredHeader` is the `Header` component or `SmallHeader`
          <ConfiguredHeader
            config={headerConfig}
            variation={variation}
            showMenu={showSmallHeaderMenu}
            enableSearch={enableSearch}
            hidePhoneNumber={hideHeaderPhoneNumber}
            isCorporate={isCorporate}
            invertSmallHeader={invertHeaderOrSmallHeader}
            invert={invertHeaderOrSmallHeader}
            withAbsoluteLinks={withAbsoluteLinks}
            absoluteBaseUrl={absoluteBaseUrl}
          />
        )}

        <Target id="content" component={MainGrowLayoutContent} flex={flex}>
          {children}
        </Target>

        {footer && isReactNode(footer) ? (
          footer
        ) : (
          <ConfiguredFooter config={footerConfig} variation={variation} isCorporate={isCorporate} nzShielded={nzShielded} withAbsoluteLinks={withAbsoluteLinks} absoluteBaseUrl={absoluteBaseUrl} />
        )}

        {enableNibby && <ChatLauncher {...nibbyOptions} />}
      </GrowLayout.Wrapper>
    </Wrapper>
  );
};

HeaderFooterLayout.propTypes = {
  children: PropTypes.node,
  variation: PropTypes.oneOf(validVariationValues),
  header: PropTypes.any,
  footer: PropTypes.any,
  smallLayout: PropTypes.bool,
  flex: PropTypes.bool,
  showSmallHeaderMenu: PropTypes.bool,
  enableSearch: PropTypes.bool,
  hideHeaderPhoneNumber: PropTypes.bool,
  isCorporate: PropTypes.bool,
  invertSmallHeader: PropTypes.bool,
  invert: PropTypes.bool,
  nzShielded: PropTypes.node,
  withAbsoluteLinks: PropTypes.bool,
  absoluteBaseUrl: PropTypes.string
};

HeaderFooterLayout.defaultProps = {
  variation: 'arhi',
  smallLayout: false,
  showSmallHeaderMenu: false,
  isCorporate: false,
  invertSmallHeader: false,
  invert: false,
  withAbsoluteLinks: false
};

HeaderFooterLayout.displayName = 'HeaderFooterLayout';

export default HeaderFooterLayout;
